import { SignupInput as StyledInput, SignupAddressInput } from './signupInputStyles';
import type { SocialSignupState } from '../Signup';
import { normalizeSignupPhone } from '@/utils/signupPhone';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from 'react-hook-form';
import { DentalSignUpValidate } from '@/utils/form.utils';
import * as yup from "yup";
import Container from '@/components/common/elements/Container';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useIsMobile, useValidateInput } from '@/common/hooks';
import { useSetRecoilState } from 'recoil';
import { toastState } from '@/state/toastState';
import { SignupMeta, ClinicsSignUpRequest, CLINIC_ALREADY_REGISTERED_CODE } from '@/api/repository/types/types';
import { useNavigate } from 'react-router-dom';
import ClinicExistingAccountNotice from './ClinicExistingAccountNotice';
import { CLINIC_EMAIL_CHANGE_PATH, type ClinicEmailChangeRouteState } from '../clinicEmailChange';
import SelectBox, { Option } from '@/components/common/elements/SelectBox';
import AddressSearchFiled from '@/components/common/organism/AddressSearchFiled';
import { KakaoAddressDocument } from '@/common/types';
import { filterNumericInput } from '@/utils/filterNumberInput';
import { convertToDate } from '@/utils/convertToDate';
import { addressCodeFields } from '@/utils/regionLabel';
import RegionSelector from '@/components/common/organism/RegionSelector';
import { api } from '@/api/repository/axios';
import { useResultModal } from '@/hooks/useResultModal';
import ClinicHomepagePhotoSection, { HomepageAddressHint, isSameAddress } from '@/components/domain/clinic/ClinicHomepagePhotoSection';
import { HOMEPAGE_PHOTO_BUSY_MESSAGE, useHomepagePhotoAuto } from '@/components/domain/clinic/useHomepagePhotoAuto';
import { navigationService } from '@/common/navigationService';
import { DEFAULT_CLINIC_IMAGE, signupImagesOf } from '@/components/domain/clinic/homepagePhotoRules';
import type { HospitalStepResult } from './ClinicHospitalVerifyStep';
import { businessNumberDigits, formatBusinessNumber } from '@/utils/clinicFields';

import { designSystem } from '@/styles/theme';

// Styled Components - 속닥속닥 스타일
const FormContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 8px 0;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
`;

const Label = styled.label`
    font-size: 0.95rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;

    span {
        color: #dc2626;
        margin-left: 2px;
    }
`;

const HelpText = styled.p`
    font-size: 0.85rem;
    color: #6b7280;
    margin: 0 0 8px 0;
`;



const StyledTextArea = styled.textarea<{ hasError?: boolean }>`
    width: 100%;
    padding: 14px 16px;
    border: none;
    border-bottom: 1px solid ${props => props.hasError ? '#dc2626' : '#e5e7eb'};
    border-radius: 0;
    font-size: 1rem;
    color: #1a1a1a;
    background-color: #fff;
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;
    resize: vertical;
    min-height: 100px;
    font-family: inherit;

    &:focus {
        border-color: ${props => props.hasError ? '#dc2626' : designSystem.colors.primary};
    }

    &::placeholder {
        color: #9ca3af;
    }

    @media (max-width: 768px) {
        padding: 12px 14px;
        font-size: 0.95rem;
    }
`;



const ErrorMessage = styled.p`
    font-size: 0.85rem;
    color: #dc2626;
    margin: 4px 0 0 0;
`;

const InputWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const ValidMessage = styled.span<{ color?: string }>`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.85rem;
    font-weight: 600;
    color: ${props => props.color};
    white-space: nowrap;
    pointer-events: none;
`;

const SectionDivider = styled.div`
    height: 1px;
    background-color: #f0f0f0;
    margin: 8px 0;
`;

const SectionTitle = styled.h2`
    font-size: 1.1rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 16px 0 0 0;
    padding-bottom: 8px;
    border-bottom: 2px solid ${designSystem.colors.primary};
    display: inline-block;
`;

const ButtonGroup = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    justify-content: center;
    padding-top: 24px;
    margin-top: 16px;
    border-top: 1px solid #f0f0f0;
`;

const CancelButton = styled.button`
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 12px 24px;
    background-color: #f3f4f6;
    color: #374151;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    transition: all 0.2s ease;

    &:hover {
        background-color: #e5e7eb;
    }

    @media (max-width: 768px) {
        flex: 1;
        justify-content: center;
        padding: 10px 20px;
        font-size: 14px;
    }
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
    white-space: nowrap;
    padding: 12px 24px;
    background-color: ${props => props.disabled ? '#e2e8f0' : designSystem.colors.primary};
    color: ${props => props.disabled ? '#94a3b8' : '#ffffff'};
    border: none;
    border-radius: 8px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    font-size: 15px;
    font-weight: 600;
    transition: all 0.2s ease;
    box-shadow: ${props => props.disabled ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.2)'};

    &:hover {
        background-color: ${props => props.disabled ? '#e2e8f0' : designSystem.colors.primaryDark};
    }

    @media (max-width: 768px) {
        flex: 1;
        padding: 10px 20px;
        font-size: 14px;
    }
`;

/** 라벨 옆 "(선택)" — 라벨의 span(빨간 *)과 다른 흐린 글자 */
const OptionalMark = styled.small`
    margin-left: 4px;
    font-size: 0.8rem;
    font-weight: normal;
    color: #6b7280;
`;

/** 홈페이지 칸 — 폰에서도 16px(입력할 때 화면이 확대되지 않게) */
const HomepageInput = styled(StyledInput)`
    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const SelectWrapper = styled.div`
    position: relative;
    width: 100%;
`;

/** 보조 버튼(흰 바탕·청록 테두리) — 홈페이지에서 가져오기·제안 채우기 */
const SubButton = styled.button`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 16px;
    border: 1px solid ${designSystem.colors.primary};
    border-radius: 8px;
    background: #fff;
    color: #2F6B66;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

import { TermsId, TERMS_VERSION } from '@/pages/Signup/termsContent';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';

/** 가입 완료 화면에 넘기는 값 — 인증 완료면 채용 공고 예시 카드를 이 값으로 그린다 */
export interface ClinicSignupDone {
    clinicVerified: boolean;
    clinicName: string;
    address: string;
    thumbnail: string;
}

interface UserInputProps {
    /** 이전(병원 인증 단계로) */
    onBack: () => void;
    onDone: (done: ClinicSignupDone) => void;
    agreements?: Record<TermsId, boolean>;
    /** 병원 인증 단계 결과 — 이메일·사업자등록번호·대표자·개업연월일과 확인 결과, 미리 채울 값 */
    hospitalStep: HospitalStepResult;
    socialState?: SocialSignupState | null;
}

type DentalSignUpFormData = yup.InferType<typeof DentalSignUpValidate>;

const SummaryBox = styled.section`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    border-radius: 12px;
    border: 1px solid #D3DBD8;
    background: #F5F7F6;
    font-size: 14px;
    color: #1F2A28;

    p {
        margin: 0;
        line-height: 1.5;
        word-break: keep-all;
    }

    span {
        color: #4F5B58;
    }
`;

const SubmitNote = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: #4F5B58;
    text-align: center;
    word-break: keep-all;
`;

/** 'YYYYMMDD' → 그날 0시 ISO(개원일 칸 값) */
const isoOfDigits = (digits: string): string => {
    const date = convertToDate(digits);
    return date ? date.toISOString() : '';
};

const DentalSignUpForm = ({ onBack, onDone, agreements, hospitalStep, socialState }: UserInputProps) => {
    const hospital = hospitalStep.hospital ?? null;
    const isSocialSignup = !!socialState?.socialAccessToken;
    const initialPhone = normalizeSignupPhone(socialState?.socialProfile.phoneNumber);
    const isMobile = useIsMobile();
    const setToast = useSetRecoilState(toastState);
    const { showError, showErrorMessage, resultModal } = useResultModal();
    const [isSubmitting, setIsSubmitting] = useState(false);
    /** 최종 가입이 '이미 가입된 치과'(code 1222)로 막힘 — 회원가입 버튼 위에 안내 */
    const [alreadyRegistered, setAlreadyRegistered] = useState<boolean>(false);
    const navigate = useNavigate();
    const [totalWorkers, setTotalWorkers] = useState("");
    // 개원일 — 확인된 병원의 심평원 개설일, 없으면 병원 인증의 개업연월일로 미리 채운다(다르면 고친다)
    const sinceDigits: string = /^\d{8}$/.test(hospital?.establishedOn ?? '') ? (hospital?.establishedOn as string) : hospitalStep.openingDate;
    const [since, setSince] = useState(sinceDigits);
    // 주소 — 병원 인증 단계에서 고른 값 그대로(주소 검색 부품이 기본값으로 다시 찾아 지역·좌표를 채운다, 고칠 수 있다 — 고치면 가입 때 서버가 다시 대조)
    const [roadAddressName, setRoadAddressName] = useState<string>(hospitalStep.address ?? hospitalStep.hospital?.address ?? "");
    /** 고른 주소의 카카오 b_code 원문 — 지역 판정은 서버가 한다 */
    const [addressCode, setAddressCode] = useState<string | undefined>(undefined);
    const [signupMeta, setSignupMeta] = useState<SignupMeta | null>(null);
    const [hospitalType, setHospitalType] = useState<Option>({ description: "", type: "" });
    const [isHospitalTypeOpen, setIsHospitalTypeOpen] = useState(false);
    const [position, setPosition] = useState({ latitude: 0, longitude: 0 });
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const [passwordConfirm, setPasswordConfirm] = useState<string>('');
    // 담당자 이메일(managerEmail)이 로그인 이메일 → 인증 완료 여부
    // 이메일은 병원 인증 단계에서 인증했다
    const emailVerified: boolean = true;

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        setValue,
        watch,
    } = useForm({
        resolver: yupResolver(DentalSignUpValidate),
        context: { socialSignup: isSocialSignup },
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            password: '',
            signupPhone: initialPhone,
            hospitalName: hospital?.name ?? hospitalStep.clinicName,
            since: isoOfDigits(sinceDigits),
            ownerName: hospitalStep.ownerName,
            licenseNumber: hospitalStep.businessNumber,
            email: hospitalStep.email,
            recruitmentManagerName: socialState?.socialProfile.name ?? '',
            recruitmentManagerEmail: hospitalStep.email,
            recruitmentManagerTel: initialPhone,
            address: '',
            detailAddress: '',
            subwayStation: [],
            hospitalType: '',
            hospitalDescription: '',
            employeeDescription: '',
            url: hospital?.homepage ?? hospitalStep.homepageUrl ?? '',
            thumbnailImage: '',
        },
    });

    const watchValue = watch();

    // 홈페이지 대표 사진 자동 등록 — 2단계가 열릴 때·홈페이지 칸에서 벗어날 때 읽어 첫 사진을 대표로(직접 올린 사진은 지킨다)
    const photo = useHomepagePhotoAuto({
        mode: 'signup',
        url: watchValue.url ?? '',
        readPreview: (url) => api.signup.homepagePreview(watchValue.email ?? '', url, socialState),
        importPhoto: (imageUrl) => api.signup.homepageThumbnail(watchValue.email ?? '', imageUrl, socialState),
    });
    const homepagePreview = photo.preview;
    const homepageFoundAddress: string | undefined = homepagePreview && !homepagePreview.failure ? homepagePreview.addresses[0] : undefined;
    const homepageAddressRaw: string | undefined = homepageFoundAddress ?? hospitalStep.homepageAddresses?.[0];
    const homepageAddress: string | null = homepageAddressRaw && !isSameAddress(homepageAddressRaw, roadAddressName) ? homepageAddressRaw : null;

    // 대표 사진([0])을 폼 값으로도 둔다 — 빠진 칸 확인·스크롤이 이 이름을 쓴다
    useEffect(() => {
        setValue('thumbnailImage', photo.state.images[0] ?? '', { shouldValidate: true });
    }, [photo.state.images, setValue]);

    // 2단계가 처음 열릴 때 — 홈페이지가 채워져 있으면 바로 읽고, 없으면 기본 이미지
    useEffect(() => {
        photo.readHomepage(watchValue.url ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // react-hook-form 필드명 → 사용자 표시 라벨 (handleInvalidSubmit 알림에 사용)
    const FIELD_LABEL_MAP: Record<string, string> = {
        password: '비밀번호',
        hospitalName: '치과명',
        hospitalType: '치과 구분',
        since: '개원일',
        ownerName: '대표자명',
        recruitmentManagerName: '채용담당자',
        recruitmentManagerEmail: '채용담당자 이메일',
        recruitmentManagerTel: '채용담당자 전화번호',
        address: '주소',
        detailAddress: '상세주소',
        licenseNumber: '사업자등록번호',
        url: '치과 홈페이지',
    };

    const scrollToError = (fieldName: keyof DentalSignUpFormData | string) => {
        const target = document.querySelector(
            `[name="${fieldName}"], [data-field="${fieldName}"]`
        ) as HTMLElement | null;
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => target.focus?.(), 300);
        }
        // fallback 으로 userIdRef 로 가지 않음 — 사용자가 왜 거기로 갔는지 모를 수 있음.
        // 누락 필드 알림은 handleInvalidSubmit / onSubmit 의 alert 로 처리.
    };

    const FIELD_NAME_MAP: Record<string, string> = {
        "비밀번호": "password",
        "치과명": "hospitalName",
        "개원일": "since",
        "대표자명": "ownerName",
        "채용담당자": "recruitmentManagerName",
        "채용담당자 이메일": "recruitmentManagerEmail",
        "채용담당자 전화번호": "recruitmentManagerTel",
        "주소": "address",
        "사업자등록번호": "licenseNumber",
        "치과 홈페이지": "url",
        "사업자등록증 사진": "businessCert",
        "대표 사진": "thumbnailImage",
    };

    const handleInvalidSubmit = (errors: any) => {
        const errorKeys = Object.keys(errors);
        if (errorKeys.length === 0) return;
        const firstErrorField = errorKeys[0] as keyof DentalSignUpFormData;
        scrollToError(firstErrorField);
    };

    const validateFields = () => {
        const fieldsToCheck = [
            { value: isSocialSignup || watchValue.password, name: "비밀번호" },
            { value: watchValue.hospitalName, name: "치과명" },
            { value: since, name: "개원일" },
            { value: watchValue.ownerName, name: "대표자명" },
            { value: watchValue.recruitmentManagerName, name: "채용담당자" },
            { value: watchValue.recruitmentManagerEmail, name: "채용담당자 이메일" },
            { value: watchValue.recruitmentManagerTel, name: "채용담당자 전화번호" },
            { value: roadAddressName, name: "주소" },
            { value: watchValue.licenseNumber, name: "사업자등록번호" },
            { value: watchValue.thumbnailImage, name: "대표 사진" },
        ];

        return fieldsToCheck
            .filter(field => !field.value || (Array.isArray(field.value) && field.value.length === 0))
            .map(field => field.name);
    };

    const onSubmit = (data: DentalSignUpFormData) => {
        const updatedMissingFields = validateFields();

        if (updatedMissingFields.length) {
            setMissingFields(updatedMissingFields);
            const firstField = FIELD_NAME_MAP[updatedMissingFields[0]];
            if (firstField) {
                scrollToError(firstField as keyof DentalSignUpFormData);
            }
            return;
        }

        // 비밀번호 확인 일치 검증 — 빈 값/불일치 모두 스크롤 + 필드 빨간 글씨(조용한 차단 방지)
        if (!isSocialSignup && (!passwordConfirm || passwordConfirm !== watchValue.password)) {
            setMissingFields(['비밀번호 확인']);
            scrollToError('password');
            return;
        }

        // 로그인 이메일 인증 미완료 시 해당 필드로 스크롤 + 필드 빨간 글씨
        if (!emailVerified) {
            setMissingFields(['이메일']);
            scrollToError('email');
            return;
        }

        const payload = {
            signupVersion: 2,
            phoneNumber: normalizeSignupPhone(watchValue.signupPhone),
            email: watchValue.email,
            password: isSocialSignup ? crypto.randomUUID() : watchValue.password,
            socialAccessToken: socialState?.socialAccessToken,
            socialProvider: socialState?.socialProvider,
            clinic: {
                name: watchValue.hospitalName,
                since: watchValue.since,
                phoneNumber: normalizeSignupPhone(watchValue.signupPhone),
                ownerName: watchValue.ownerName,
                managerName: watchValue.recruitmentManagerName,
                managerEmail: watchValue.recruitmentManagerEmail,
                managerPhoneNumber: watchValue.recruitmentManagerTel,
                description: watchValue.hospitalDescription,
                totalWorkers: Number(totalWorkers),
                totalWorkersDescription: watchValue.employeeDescription,
                address: roadAddressName,
                addressDetail: watchValue.detailAddress,
                districtId: watchValue.address,
                addressCode: addressCode,
                stationIds: watchValue.subwayStation,
                url: watchValue.url ?? '',
                // 병원 인증 단계 결과 — 서버가 다시 검사한다
                hospitalCheckToken: hospital?.token ?? undefined,
                homepageCheckToken: hospitalStep.homepageToken ?? undefined,
                businessCertToken: hospitalStep.businessCertToken ?? undefined,
                // 병원 인증 단계의 사업자등록증 개업연월일 — 국세청 진위확인용(개원일 칸은 심평원 개설일로 채워질 수 있다)
                ntsOpeningDate: /^\d{8}$/.test(hospitalStep.openingDate ?? '') ? hospitalStep.openingDate : undefined,
                // 백엔드 DTO(ApiClinicRequest)는 thumbnail 이 아니라 images/mobileImages 를 받는다.
                // 대표 사진이 [0], 그 뒤 추가 사진 순서 그대로(5장까지 — 서버 ClinicImageRule 이 다시 검사)
                images: signupImagesOf(photo.state),
                mobileImages: signupImagesOf(photo.state),
                latitude: position.latitude,
                longitude: position.longitude,
                clinicType: watchValue.hospitalType,
                // 사업자등록번호는 새 칸(business_number) — 면허번호 칸과 다르다
                businessNumber: businessNumberDigits(watchValue.licenseNumber),
            },
            authority: 'ROLE_CLINIC',
            agreements: agreements,
            termsVersion: TERMS_VERSION,
        };

        setIsSubmitting(true);
        setAlreadyRegistered(false);
        // 폼 검증을 통과한 값이므로 필수 필드는 실제로는 채워져 있음 (RHF 값 타입은 string | undefined)
        api.clinicsSignUp(payload as ClinicsSignUpRequest)
            .then((res) => {
                if (res.message === "성공") {
                    onDone({
                        clinicVerified: !!res.result?.clinicVerified,
                        clinicName: watchValue.hospitalName ?? '',
                        address: roadAddressName,
                        thumbnail: signupImagesOf(photo.state)[0] ?? DEFAULT_CLINIC_IMAGE,
                    });
                } else if (res.code === CLINIC_ALREADY_REGISTERED_CODE) {
                    showAlreadyRegistered();
                } else {
                    showErrorMessage(res.message || '회원가입에 실패했습니다.');
                }
            })
            .catch((error) => {
                if (error?.response?.data?.code === CLINIC_ALREADY_REGISTERED_CODE) {
                    showAlreadyRegistered();
                    return;
                }
                console.error('clinicsSignUp error:', error);
                // 서버 원문(트랜잭션/스택 등) 노출 방지 — 정책에 따라 안전한 문구로. 미정의 에러는 일반 문구.
                showError(error, '회원가입 실패');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    /** 이미 가입된 치과 안내를 띄우고 그 자리로 */
    const showAlreadyRegistered = () => {
        setAlreadyRegistered(true);
        requestAnimationFrame(() => {
            document.querySelector('[data-existing-clinic]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    };

    /** [가입 이메일 바꾸기] — 병원 인증 단계 값(가린 이메일은 없음)을 라우터 상태로 넘긴다 */
    const goEmailChange = () => {
        const state: ClinicEmailChangeRouteState = {
            email: hospitalStep.email,
            businessNumber: hospitalStep.businessNumber,
            ownerName: hospitalStep.ownerName,
            clinicName: hospitalStep.clinicName,
            address: hospitalStep.address ?? null,
            hospitalToken: hospital?.token ?? null,
            maskedEmail: null,
            provider: null,
            credentials: socialState?.socialAccessToken ? { socialProvider: socialState.socialProvider, socialAccessToken: socialState.socialAccessToken } : null,
        };
        navigate(CLINIC_EMAIL_CHANGE_PATH, { state });
    };

    const handleUserTypeChange = (selected: Option) => {
        setHospitalType(selected);
        setValue("hospitalType", selected.type);
    };

    const handleSinceChange = (value: string) => {
        const filterNumber = filterNumericInput(value);
        const convertToDateValue = convertToDate(filterNumber);
        if (convertToDateValue) {
            const isoString = convertToDateValue.toISOString();
            setValue("since", isoString);
        }
        setSince(filterNumber);
    };

    const handleAddress = (address: KakaoAddressDocument) => {
        const fields = addressCodeFields(address.address?.b_code);
        setValue("address", fields.districtId ?? '');
        setAddressCode(fields.addressCode);
        setRoadAddressName(address.address_name);
        const pos = {
            latitude: address.y,
            longitude: address.x
        };
        setPosition(pos);
    };

    // missingFields 자동 클린업: 사용자가 누락 필드를 채우면 빨간 underline 즉시 제거
    useEffect(() => {
        if (missingFields.length === 0) return;
        const stillMissing: string[] = missingFields.filter((f) => {
            if (f === '비밀번호') return !watchValue.password;
            if (f === '비밀번호 확인') return !passwordConfirm || passwordConfirm !== watchValue.password;
            if (f === '치과명') return !watchValue.hospitalName;
            if (f === '개원일') return !since;
            if (f === '대표자명') return !watchValue.ownerName;
            if (f === '채용담당자') return !watchValue.recruitmentManagerName;
            if (f === '채용담당자 이메일') return !watchValue.recruitmentManagerEmail;
            if (f === '채용담당자 전화번호') return !watchValue.recruitmentManagerTel;
            if (f === '주소') return !roadAddressName;
            if (f === '사업자등록번호') return !watchValue.licenseNumber;
            if (f === '대표 사진') return !watchValue.thumbnailImage;
            return true;
        });
        if (stillMissing.length !== missingFields.length) {
            setMissingFields(stillMissing);
        }
    }, [
        watchValue.password, passwordConfirm,
        watchValue.hospitalName, since, watchValue.signupPhone,
        watchValue.ownerName, watchValue.recruitmentManagerName,
        watchValue.recruitmentManagerEmail, watchValue.recruitmentManagerTel,
        roadAddressName, watchValue.thumbnailImage, watchValue.licenseNumber, watchValue.url,
    ]);

    useEffect(() => {
        const fetchClinicsSignupMeta = async () => {
            try {
                const response = await api.signup.clinicsMeta();
                setSignupMeta(response.result);
            } catch (error) {
                console.error("Failed to fetch clinics signup meta:", error);
            }
        };

        fetchClinicsSignupMeta();
    }, []);

    return (
        <FormContent>
            {resultModal}
            <SectionTitle>기본정보</SectionTitle>

            <SummaryBox aria-label="병원 인증 정보">
                <p><span>로그인 이메일</span> {hospitalStep.email}</p>
                <p><span>사업자등록번호</span> {formatBusinessNumber(hospitalStep.businessNumber)} · <span>대표자</span> {hospitalStep.ownerName}</p>
                {hospital && <p><span>확인된 병원</span> {hospital.name}</p>}
                <SubButton type="button" onClick={onBack} style={{ alignSelf: 'flex-start', marginTop: 4 }}>병원 인증 정보 바꾸기</SubButton>
            </SummaryBox>

            <FormGroup>
                <Label htmlFor="signup-phone">가입자 휴대전화번호<span>*</span></Label>
                <Controller name="signupPhone" control={control} render={({ field }) => (
                    <StyledInput {...field} id="signup-phone" inputMode="tel" autoComplete="tel"
                        placeholder="01012345678" hasError={!!errors.signupPhone}
                        onChange={(e) => {
                            const next = normalizeSignupPhone(e.target.value).slice(0, 11);
                            if (!watchValue.recruitmentManagerTel || watchValue.recruitmentManagerTel === field.value) {
                                setValue('recruitmentManagerTel', next, { shouldValidate: true });
                            }
                            field.onChange(next);
                        }} />
                )} />
                <HelpText>가입하는 대표자 또는 담당자의 휴대전화번호를 입력해 주세요.</HelpText>
                {errors.signupPhone && <ErrorMessage>{errors.signupPhone.message}</ErrorMessage>}
            </FormGroup>
            {!isSocialSignup && <>
            <FormGroup>
                <Label>비밀번호<span>*</span></Label>
                <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            type="password"
                            placeholder="비밀번호를 입력해주세요"
                            hasError={missingFields.includes('비밀번호') || !!errors.password}
                        />
                    )}
                />
                {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>비밀번호 확인<span>*</span></Label>
                <InputWrapper>
                    <StyledInput
                        type="password"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        placeholder="비밀번호를 다시 한 번 입력해주세요"
                        hasError={missingFields.includes('비밀번호 확인') || (passwordConfirm.length > 0 && passwordConfirm !== watchValue.password)}
                    />
                    {passwordConfirm.length > 0 && (
                        <ValidMessage color={passwordConfirm === watchValue.password ? '#10b981' : '#dc2626'}>
                            {passwordConfirm === watchValue.password ? '일치' : '불일치'}
                        </ValidMessage>
                    )}
                </InputWrapper>
                {passwordConfirm.length === 0 && missingFields.includes('비밀번호 확인') && (
                    <ErrorMessage>비밀번호 확인을 입력해주세요.</ErrorMessage>
                )}
            </FormGroup>

            </>}
            <FormGroup>
                <Label>치과명<span>*</span></Label>
                <Controller
                    name="hospitalName"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="치과명을 입력해주세요"
                            hasError={missingFields.includes('치과명') || !!errors.hospitalName}
                        />
                    )}
                />
                {errors.hospitalName && <ErrorMessage>{errors.hospitalName.message}</ErrorMessage>}
            </FormGroup>

            {/* 치과 이미지 — 치과 정보 수정과 같은 공용 부품·순서(홈페이지 → 안내 → 사진, 2026-10-30) */}
            <SectionDivider />
            <SectionTitle>치과 이미지</SectionTitle>
            <HelpText style={{ margin: '-8px 0 16px' }}>치과를 소개할 16:9 이미지를 등록해 주세요(최대 5장).</HelpText>

            <div data-field="thumbnailImage">
                <ClinicHomepagePhotoSection
                    photo={photo}
                    label={<Label htmlFor="signup-homepage">치과 홈페이지<OptionalMark>(선택)</OptionalMark></Label>}
                    input={(
                        <Controller
                            name="url"
                            control={control}
                            render={({ field }) => (
                                <HomepageInput
                                    {...field}
                                    id="signup-homepage"
                                    inputMode="url"
                                    placeholder="예: www.치과.com"
                                    hasError={!!errors.url}
                                    onBlur={() => {
                                        field.onBlur();
                                        // 칸에서 벗어날 때 읽는다 — 같은 주소는 다시 읽지 않는다(주소가 없으면 읽지 않는다)
                                        photo.readHomepage(field.value ?? '');
                                    }}
                                />
                            )}
                        />
                    )}
                    url={String(watchValue.url ?? '')}
                    onReload={() => photo.readHomepage(String(watchValue.url ?? ''), { force: true })}
                    error={errors.url && <ErrorMessage>{errors.url.message}</ErrorMessage>}
                    photoHasError={missingFields.includes('대표 사진')}
                    photoError={missingFields.includes('대표 사진') && <ErrorMessage>대표 사진을 넣어 주세요.</ErrorMessage>}
                />
            </div>

            <SectionDivider />
            <SectionTitle>치과 정보</SectionTitle>

            <FormGroup>
                <Label>개원일<span>*</span></Label>
                <StyledInput
                    data-field="since"
                    name="since"
                    value={since}
                    onChange={(e) => handleSinceChange(e.target.value)}
                    placeholder="YYYYMMDD"
                    maxLength={8}
                    hasError={missingFields.includes('개원일') || !!errors.since}
                />
                {errors.since && <ErrorMessage>{errors.since.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>총 직원 수</Label>
                <StyledInput
                    value={totalWorkers}
                    onChange={(e) => setTotalWorkers(e.target.value)}
                    placeholder="총 직원 수를 입력해주세요"
                />
            </FormGroup>

            <FormGroup>
                <Label>치과 유형<span>*</span></Label>
                <SelectWrapper>
                    <Controller
                        name="hospitalType"
                        control={control}
                        render={({ field }) => (
                            <StyledInput
                                {...field}
                                value={hospitalType.description ?? ""}
                                placeholder="치과 유형을 선택해주세요"
                                readOnly
                                style={{ cursor: 'pointer' }}
                                onFocus={() => setIsHospitalTypeOpen(!isHospitalTypeOpen)}
                            />
                        )}
                    />
                    <SelectBox
                        onOpenContrall={(open) => setIsHospitalTypeOpen(open)}
                        options={signupMeta?.clinicTypes ?? null}
                        onChange={(selected) => handleUserTypeChange(selected)}
                        isOpen={isHospitalTypeOpen}
                    />
                </SelectWrapper>
            </FormGroup>

            <FormGroup>
                <Label>치과 설명</Label>
                <Controller
                    name="hospitalDescription"
                    control={control}
                    render={({ field }) => (
                        <StyledTextArea
                            {...field}
                            placeholder="치과 소개 및 설명을 입력해주세요"
                        />
                    )}
                />
            </FormGroup>

            <SectionDivider />
            <SectionTitle>채용담당자 정보</SectionTitle>

            <FormGroup>
                <Label>담당자명<span>*</span></Label>
                <Controller
                    name="recruitmentManagerName"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="채용담당자명을 입력해주세요"
                            hasError={missingFields.includes('채용담당자') || !!errors.recruitmentManagerName}
                        />
                    )}
                />
                {errors.recruitmentManagerName && <ErrorMessage>{errors.recruitmentManagerName.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>담당자 이메일<span>*</span></Label>
                <Controller
                    name="recruitmentManagerEmail"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            type="email"
                            placeholder="담당자 이메일을 입력해주세요"
                            hasError={missingFields.includes('채용담당자 이메일') || !!errors.recruitmentManagerEmail}
                        />
                    )}
                />
                {errors.recruitmentManagerEmail && <ErrorMessage>{errors.recruitmentManagerEmail.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>담당자 휴대전화번호<span>*</span></Label>
                <Controller
                    name="recruitmentManagerTel"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="지원자 알림을 받을 휴대전화번호"
                            inputMode="tel"
                            onChange={(e) => field.onChange(normalizeSignupPhone(e.target.value).slice(0, 11))}
                            hasError={missingFields.includes('채용담당자 전화번호') || !!errors.recruitmentManagerTel}
                        />
                    )}
                />
                <HelpText>지원자 알림을 받을 번호입니다. 가입자 번호와 다르게 지정할 수 있습니다.</HelpText>
                {errors.recruitmentManagerTel && <ErrorMessage>{errors.recruitmentManagerTel.message}</ErrorMessage>}
            </FormGroup>

            <SectionDivider />
            <SectionTitle>주소 정보</SectionTitle>

            <FormGroup data-field="address">
                <Label>주소<span>*</span></Label>
                {/* 홈페이지에서 찾은 주소 — 이 화면에서 읽은 홈페이지 먼저, 없으면 병원 인증 단계 값. 고른 주소와 같으면 숨김(치과 정보 수정과 같다) */}
                {homepageAddress && <HomepageAddressHint address={homepageAddress} />}
                <SignupAddressInput><AddressSearchFiled
                    defaultValue={roadAddressName}
                    onChange={handleAddress}
                    height={48}
                    width={'100%'}
                    border={'none'}
                    borderBottom={'1px solid #e5e7eb'}

                    margin='0'
                /></SignupAddressInput>
                {errors.address && <ErrorMessage>{errors.address.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>상세주소<span>*</span></Label>
                <Controller
                    name="detailAddress"
                    control={control}
                    render={({ field }) => (
                        <StyledInput
                            {...field}
                            placeholder="상세주소를 입력해주세요"
                            hasError={!!errors.detailAddress}
                        />
                    )}
                />
                {errors.detailAddress && <ErrorMessage>{errors.detailAddress.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
                <Label>인근 지하철역<OptionalMark>(선택)</OptionalMark></Label>
                <RegionSelector
                    onSelectedSubwayItem={(item) => setValue("subwayStation", item)}
                />
            </FormGroup>

            <SectionDivider />
            <SectionTitle>추가 정보</SectionTitle>

            <FormGroup>
                <Label>직원 설명</Label>
                <Controller
                    name="employeeDescription"
                    control={control}
                    render={({ field }) => (
                        <StyledTextArea
                            {...field}
                            placeholder="직원 구성 및 업무 설명을 입력해주세요"
                        />
                    )}
                />
            </FormGroup>

            {alreadyRegistered && (
                <ClinicExistingAccountNotice account={null} clinicName={hospitalStep.clinicName} onEmailChange={goEmailChange} />
            )}
            <SubmitNote>입력하신 치과 정보가 확인되면 가입과 함께 치과 인증이 끝나 바로 채용 공고를 올릴 수 있어요.</SubmitNote>
            <ButtonGroup>
                <CancelButton type="button" onClick={onBack}>
                    <KeyboardBackspaceIcon fontSize="small" /> 이전
                </CancelButton>
                <SubmitButton
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => {
                        // 홈페이지 읽기·사진 저장 중이면 기다리게(최대 25초 뒤 풀림) — 처음 열자마자 눌러도 '대표 사진 빠짐'으로 막지 않는다
                        if (photo.busy) {
                            navigationService.showToast(HOMEPAGE_PHOTO_BUSY_MESSAGE, 'info');
                            return;
                        }
                        // 치과 이미지 5장까지 — 넘으면 사진 칸 빨간 안내 + 알림, 사진 칸으로
                        const photoLimit: string | null = photo.limitError();
                        if (photoLimit) {
                            navigationService.showToast(photoLimit, 'error');
                            document.querySelector('[data-field="thumbnailImage"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            return;
                        }
                        handleSubmit(onSubmit, handleInvalidSubmit)();
                    }}
                >
                    {isSubmitting ? <ButtonSpinner /> : '회원가입'}
                </SubmitButton>
            </ButtonGroup>
        </FormContent>
    );
};

export default DentalSignUpForm;
